import { Draggable, Droppable } from '@hello-pangea/dnd'
import { CustomInputGroups, DeviceSize } from "types"
import { ReactComponent as DeleteIcon } from "assets/icons/delete.svg"
import { ReactComponent as AddIcon } from "assets/icons/add.svg"
import { ReactComponent as DragHandleIcon } from "assets/icons/drag_handle.svg"
import { ReactComponent as EditIcon } from "assets/icons/edit.svg"
import { useTranslation } from "react-i18next"
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import useWindowDimensions from 'hooks/useWindowDimensions'
import EditCustomInputGroupForm from './editCustomInputGroupForm'
import DeleteCustomInputGroupForm from './deleteCustomInputGroupForm'
import EditCustomInputForm from './editCustomInputForm'
import Section from 'components/layout/section'
import SectionHeader from 'components/layout/sectionHeader'
import SectionHeaderTitle from 'components/layout/sectionHeaderTitle'
import SectionHeaderActions from 'components/layout/sectionHeaderActions'
import useLayout from 'hooks/useLayout'
import TableList from 'components/list/tableList/tableList'
import TableListRow from 'components/list/tableList/tableListRow'
import TableListCell from 'components/list/tableList/tableListCell'
import IconButton from 'components/buttons/iconButton'

interface EditCustomInputsFormGroupProps {
    groups: CustomInputGroups
    setGroups: Dispatch<SetStateAction<CustomInputGroups>>
    setCustomInputGroupOrder: Dispatch<SetStateAction<string[]>>
    groupName: string
    index: number
    isDraggingGroup: boolean
    isDraggingField: boolean
    isSubmitting?: boolean
}

const EditCustomInputsFormGroup: React.FC<EditCustomInputsFormGroupProps> = ({
    groups, 
    setGroups,
    groupName,
    index,
    isDraggingGroup,
    isDraggingField,
    setCustomInputGroupOrder,
    isSubmitting = false
}) => {

    const { t } = useTranslation()
    const { width, deviceSize } = useWindowDimensions()
    const [inputKeyFocus, setInputKeyFocus] = useState<string | null>(null)

    // Use tableWidth to calculate the width of the table
    // for more accurate drag and drop experience.
    const tableRef = useRef<HTMLDivElement>(null)
    const [tableWidth, setTableWidth] = useState(0)

    const {
        openDialog
    } = useLayout()

    // Get the width of the table on window resize.
    useEffect(() => {
        if (tableRef.current) {
            const offsetWidth = tableRef.current.offsetWidth
            setTableWidth(offsetWidth)
        }
    }, [width])

    // Handle delete custom field click.
    // No reordering required.
    const handleDeleteFieldClick = (group: string, key: string) => {
        if (isSubmitting) return
        // Remove the custom field from the group.
        let newCustomInputOrder = groups[group].customInputOrder.filter((id) => id !== key)
        let newCustomInputs = { ...groups[group].customInputs }
        delete newCustomInputs[key]
        let newGroups = {
            ...groups,
            [group]: {
                ...groups[group],
                customInputOrder: newCustomInputOrder,
                customInputs: newCustomInputs
            }
        }
        setGroups(newGroups)
    }

    // Handle add custom field clic.
    const handleAddFieldClick = () => {
        if (isSubmitting) return
        openDialog(`add_custom_field_${groupName}`, { groupName, setGroups, groups })
    }

    // Handle edit group click.
    const handleEditGroupClick = () => {
        if (isSubmitting) return
        openDialog(`edit_custom_field_group_${groupName}`, { groups, setGroups, setCustomInputGroupOrder, groupName })
    }

    // Handle delete group click.
    const handleDeleteGroupClick = () => {
        if (isSubmitting) return
        openDialog(`delete_custom_field_group_${groupName}`, { groups, setGroups, setCustomInputGroupOrder, groupName })
    }

    // Handle custom field edit click.
    const handleEditFieldClick = (groupName: string, key: string) => {
        if (isSubmitting) return
        setInputKeyFocus(key)
        openDialog(`edit_custom_field_${groupName}_${key}`, { customInputKey: key, groups, groupName, setGroups })
    }

    return (                 
        <Draggable draggableId={groupName} index={index} isDragDisabled={isSubmitting}>
            {(provided) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}>
                    <Section design="card" className='mb-2 bg-white'>
                        <SectionHeader>
                            <SectionHeaderTitle>
                                <div {...(!isSubmitting ? provided.dragHandleProps : {})}>
                                    <DragHandleIcon className={`fill-black ${isSubmitting ? 'cursor-not-allowed' : ''}`} />
                                </div>
                                <h2>{groupName}</h2>
                            </SectionHeaderTitle>
                            <SectionHeaderActions>
                                <IconButton onClick={isSubmitting ? undefined : () => handleAddFieldClick()} icon={<AddIcon />} className={isSubmitting ? 'animate-pulse bg-gray-200 cursor-not-allowed' : ''} />
                                <IconButton onClick={isSubmitting ? undefined : () => handleEditGroupClick()} icon={<EditIcon />} className={isSubmitting ? 'animate-pulse bg-gray-200 cursor-not-allowed' : ''} />
                                <IconButton onClick={isSubmitting ? undefined : () => handleDeleteGroupClick()} icon={<DeleteIcon />} className={isSubmitting ? 'animate-pulse bg-gray-200 cursor-not-allowed' : ''} />
                            </SectionHeaderActions>
                        </SectionHeader>
                        <Droppable droppableId={groupName} type="field" isDropDisabled={isSubmitting}>
                            {(provided) => (
                                <TableList
                                    highlightDragAndDrop={isDraggingField && !isSubmitting}
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                >
                                    <TableListRow isHeader={true}>
                                        <TableListCell className={`w-[56px]`}></TableListCell>  
                                        <TableListCell>{t('name')}</TableListCell>
                                        {deviceSize !== DeviceSize.SM && <>
                                            <TableListCell>{t('type')}</TableListCell>
                                            <TableListCell>{t('required')}</TableListCell>
                                            <TableListCell>{t('defaultValue')}</TableListCell>
                                        </>}
                                        <TableListCell className={`w-[56px]`}></TableListCell>
                                    </TableListRow>
                                        {groups[groupName].customInputOrder.map((key, index) => {
                                            const field = groups[groupName].customInputs[key]
                                            return (
                                                <Draggable draggableId={`${groupName}-${key}`} index={index} key={key} isDragDisabled={isSubmitting}>
                                                    {(provided, snapshot) => (
                                                        <TableListRow
                                                            className={`${snapshot.isDragging ? `bg-white` : ``} ${isSubmitting ? 'animate-pulse bg-gray-200' : ''}`}
                                                            style={{ width: `${tableWidth}px` }}
                                                            key={key}
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            hoverEffect={!isSubmitting}
                                                        >
                                                            <TableListCell {...(!isSubmitting ? provided.dragHandleProps : {})}>
                                                                <DragHandleIcon className={isSubmitting ? 'animate-pulse bg-gray-200 rounded cursor-not-allowed' : ''} />
                                                            </TableListCell>
                                                            <TableListCell>{key}</TableListCell>
                                                            {deviceSize !== DeviceSize.SM && <>
                                                                <TableListCell>{field.type}</TableListCell>
                                                                <TableListCell>{field.required ? t('yes') : t('no')}</TableListCell>
                                                                <TableListCell>{field.defaultValue}</TableListCell>
                                                            </>}
                                                            <TableListCell >
                                                                <IconButton onClick={isSubmitting ? undefined : () => handleEditFieldClick(groupName, key)} icon={<EditIcon />} size="small" className={isSubmitting ? 'animate-pulse bg-gray-200 cursor-not-allowed' : ''} />
                                                                <IconButton onClick={isSubmitting ? undefined : () => handleDeleteFieldClick(groupName, key)} icon={<DeleteIcon />} size="small" className={isSubmitting ? 'animate-pulse bg-gray-200 cursor-not-allowed' : ''} />
                                                            </TableListCell>
                                                        </TableListRow>
                                                    )}
                                                </Draggable>)
                                            })}
                                        {provided.placeholder}
                                </TableList>
                            )}
                        </Droppable>
                    </Section>
                </div>
            )}
        </Draggable>
    )

}

export default EditCustomInputsFormGroup

